/**
 * `DotRating` — selettore di valutazione "a stelle" ma con **pallini**.
 *
 * Mostra `max` pallini (default 5): quelli fino al valore selezionato sono
 * pieni, gli altri vuoti. Cliccando un pallino si imposta quel valore; passando
 * il mouse si vede un'anteprima. È un controllo pienamente accessibile
 * (pattern `radiogroup`/`radio`), navigabile da tastiera con le frecce.
 *
 * È controllato: `value` e `onChange` vivono nel genitore. Il valore minimo è
 * `1` (non si può azzerare cliccando di nuovo), coerente con i pesi `1..5`.
 */
import { useId, useState } from 'react';

interface DotRatingProps {
  /** Valore corrente (`1..max`). */
  value: number;
  /** Notifica il nuovo valore selezionato. */
  onChange: (next: number) => void;
  /** Numero di pallini (valore massimo). Default 5. */
  max?: number;
  /** Etichetta accessibile del gruppo (es. "Peso di React"). */
  label: string;
  /** Etichetta della singola posizione, per lo screen reader (es. `3 su 5`). */
  optionLabel?: (n: number, max: number) => string;
  /** Disabilita l'interazione (aspetto attenuato). */
  disabled?: boolean;
  /** Dimensione dei pallini. `md` (default) o `sm` (compatta). */
  size?: 'sm' | 'md';
  /**
   * Sola lettura: mostra i pallini senza interazione né aspetto "disabilitato".
   * Utile per visualizzare un peso già impostato (es. dettaglio competenza).
   */
  readOnly?: boolean;
}

export function DotRating({
  value,
  onChange,
  max = 5,
  label,
  optionLabel = (n, m) => `${n} su ${m}`,
  disabled = false,
  readOnly = false,
  size = 'md',
}: DotRatingProps) {
  const groupId = useId();
  const sizeClass = size === 'sm' ? ' dot-rating--sm' : '';

  // Sola lettura: rendering statico (nessun bottone, nessun tab stop), con i
  // pallini pieni fino a `value`. Espone comunque un'etichetta accessibile.
  if (readOnly) {
    const dots = Array.from({ length: max }, (_, i) => i + 1);
    return (
      <div
        className={`dot-rating dot-rating--readonly${sizeClass}`}
        role="img"
        aria-label={`${label}: ${optionLabel(value, max)}`}
      >
        {dots.map((n) => (
          <span
            key={n}
            aria-hidden="true"
            className={
              n <= value
                ? 'dot-rating__dot dot-rating__dot--filled'
                : 'dot-rating__dot'
            }
          >
            <span className="dot-rating__glyph" />
          </span>
        ))}
      </div>
    );
  }
  // Valore "in anteprima" durante l'hover/focus da tastiera; `null` = nessuna.
  const [preview, setPreview] = useState<number | null>(null);
  const shown = preview ?? value;

  const commit = (next: number) => {
    if (disabled) return;
    const clamped = Math.min(max, Math.max(1, next));
    onChange(clamped);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        commit(value + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        commit(value - 1);
        break;
      case 'Home':
        e.preventDefault();
        commit(1);
        break;
      case 'End':
        e.preventDefault();
        commit(max);
        break;
      default:
        break;
    }
  };

  const dots = Array.from({ length: max }, (_, i) => i + 1);

  return (
    <div
      className={`dot-rating${sizeClass}`}
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setPreview(null)}
      onBlur={() => setPreview(null)}
    >
      {dots.map((n) => {
        const filled = n <= shown;
        return (
          <button
            key={n}
            type="button"
            id={`${groupId}-${n}`}
            role="radio"
            aria-checked={n === value}
            aria-label={optionLabel(n, max)}
            className={
              filled ? 'dot-rating__dot dot-rating__dot--filled' : 'dot-rating__dot'
            }
            disabled={disabled}
            // I bottoni non sono nel tab order: il radiogroup ha un unico tab
            // stop e la selezione avviene con le frecce (pattern radio).
            tabIndex={-1}
            onClick={() => commit(n)}
            onMouseEnter={() => setPreview(n)}
            onFocus={() => setPreview(n)}
          >
            <span className="dot-rating__glyph" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

export default DotRating;
